import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTableSort, SortTh } from "@/lib/tableSort";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, RotateCcw, Layers, Network, Cable, PenLine, Download, RadioTower, X, ChevronDown, ChevronUp, ListTree, ArrowUp, ArrowDown, Split, Move, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { NetworkMapView, MapLegend } from "@/components/network/NetworkMapView";
import { InfraDetailPanel } from "@/components/network/InfraDetailPanel";
import { StatusPill, NoCoordBadge, LocCell } from "@/components/network/StatusPill";
import { useNetworkRefs, INFRA_STATUS, STATUS_LABEL, SELECT_CLS, pct, CABLE_JENIS_LABEL, fmtLen, downloadKml, OLT_TECH, apiError } from "@/lib/network";
import { WilayahCombobox } from "@/components/network/WilayahCombobox";
import { OltDetail } from "@/components/network/OltDetail";
import { CableDrawBar } from "@/components/network/CableDrawBar";
import { CableFormDialog } from "@/components/network/CableFormDialog";
import { CableDetailPanel } from "@/components/network/CableDetailPanel";
import { SplitterDetail } from "@/components/network/SplitterDetail";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { OtbDetail } from "@/components/network/OtbDetail";
import { Link } from "react-router-dom";
import { ServerDetail } from "@/components/network/ServerDetail";
import { FdtDetail } from "@/components/network/FdtDetail";
import { ContainerPickPanel, endpointBlock } from "@/components/network/EndpointPicker";

const CT = { server: 1, fdt: 1 };
// Ciutkan/buka daftar (desktop saja; tampilan HP tidak berubah).
const CollapseBtn = ({ open, onClick, tid }) => (
  <Button size="sm" variant="ghost" className="hidden h-7 lg:inline-flex" onClick={onClick} data-testid={tid}>
    {open ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}{open ? "Ciutkan" : "Tampilkan"}</Button>);
const cableLen = (c) => (c.same_container ? "-" : `${fmtLen(c.panjang_m)}${c.panjang_perkiraan ? " (perkiraan)" : ""}`);

const ALL = "__all";

function LocationGate({ refs, onPick }) {
  return (
    <div className="mx-auto mt-10 max-w-md rounded-lg border border-[#E5E7EB] bg-white p-6 text-center" data-testid="network-location-gate">
      <h2 className="font-display text-lg font-bold">Pilih lokasi</h2>
      <p className="mb-4 text-sm text-[#6B7280]">Lokasi absensi Anda tidak dapat ditentukan secara pasti. Pilih lokasi yang ingin dilihat.</p>
      <div className="flex flex-wrap justify-center gap-2">
        {refs.locations.filter((l) => l.active).map((l) => (
          <Button key={l.id} variant="outline" onClick={() => onPick(l.id)} data-testid={`network-gate-${l.code}`}>{l.name}</Button>
        ))}
        <Button variant="ghost" onClick={() => onPick(ALL)} data-testid="network-gate-all">Semua Lokasi</Button>
      </div>
    </div>
  );
}

const CABLE_GETTERS = { kode: (c) => c.kode, jenis: (c) => c.jenis, dari: (c) => c.from?.kode, ke: (c) => c.to?.kode,
  core: (c) => c.jumlah_core, panjang: (c) => c.panjang_m, status: (c) => c.status };

const TB = "h-10 px-3 rounded-md border text-sm font-semibold flex items-center gap-2 transition-colors";
const LAYERS = [
  ["links", Network, "Relasi", "border-[#16A34A] bg-[#F0FDF4] text-[#15803D]"],
  ["showOlt", RadioTower, "OLT", "border-[#7C3AED] bg-[#F5F3FF] text-[#6D28D9]"],
  ["showSplit", Split, "Splitter", "border-[#0D9488] bg-[#F0FDFA] text-[#0F766E]"],
  ["showCables", Cable, "Kabel", "border-[#EA580C] bg-[#FFF7ED] text-[#C2410C]"],
];
const OFF = "border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F8F9FA]";

function MapToolbar({ panel, setPanel, sat, links, showOlt, showSplit, showCables, canDraw, onDraw, canMove, move, onMove }) {
  const n = [sat, links, showOlt, showSplit, showCables].filter(Boolean).length;
  const tog = (k) => setPanel((p) => (p === k ? null : k));
  const cls = (k) => `${TB} ${panel === k ? "border-[#0055FF] bg-[#EFF6FF] text-[#0055FF]" : OFF}`;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="network-map-toolbar">
      <button type="button" className={cls("layer")} onClick={() => tog("layer")} aria-expanded={panel === "layer"} data-testid="network-layer-toggle">
        <Layers className="h-4 w-4" />Layer ({n})<ChevronDown className={`h-4 w-4 transition-transform ${panel === "layer" ? "rotate-180" : ""}`} /></button>
      <button type="button" className={cls("legend")} onClick={() => tog("legend")} aria-expanded={panel === "legend"} data-testid="network-legend-toggle">
        <ListTree className="h-4 w-4" />Legenda<ChevronDown className={`h-4 w-4 transition-transform ${panel === "legend" ? "rotate-180" : ""}`} /></button>
      {canDraw && <button type="button" className={`${TB} border-[#0055FF] bg-[#0055FF] text-white hover:bg-[#0044CC]`} onClick={onDraw} data-testid="network-draw-cable">
        <PenLine className="h-4 w-4" />Gambar Jalur</button>}
      {canMove && <button type="button" className={`${TB} ${move ? "border-[#B45309] bg-[#FEF3C7] text-[#92400E]" : OFF}`} onClick={onMove} aria-pressed={move} data-testid="network-move-toggle">
        <Move className="h-4 w-4" />{move ? "Selesai Geser" : "Geser Perangkat"}</button>}
    </div>
  );
}

function MoveBar({ target, saving, onDone }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#FCD34D] bg-[#FFFBEB] p-2 text-xs text-[#92400E]" role="status" data-testid="network-move-bar">
      <Move className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1" data-testid="network-move-hint">{target
        ? <>Klik peta untuk menempatkan <b>{target.kode}</b> (belum berkoordinat).</>
        : "Mode Geser aktif: seret marker OLT, Splitter, ODC, atau ODP ke posisi baru. Lokasi tersimpan otomatis saat marker dilepas. Untuk titik tanpa koordinat, pilih dari daftar lalu klik peta."}</span>
      {saving && <span className="flex items-center gap-1 font-semibold" data-testid="network-move-saving"><Loader2 className="h-3.5 w-3.5 animate-spin" />Menyimpan…</span>}
      <Button size="sm" variant="outline" className="h-7" onClick={onDone} data-testid="network-move-done">Selesai</Button>
    </div>
  );
}

function LayerPanel(p) {
  const set = { links: p.setLinks, showOlt: p.setShowOlt, showSplit: p.setShowSplit, showCables: p.setShowCables };
  return (
    <div className="flex flex-wrap gap-2 rounded-lg border border-[#E5E7EB] bg-white p-2" data-testid="network-map-controls">
      <button type="button" className={`${TB} ${p.sat ? "border-[#0055FF] bg-[#EFF6FF] text-[#0055FF]" : OFF}`} onClick={() => p.setSat((v) => !v)}
              aria-pressed={p.sat} data-testid="network-toggle-sat"><Layers className="h-4 w-4" />{p.sat ? "Satelit" : "Jalan"}</button>
      {LAYERS.map(([k, Icon, label, on]) => (
        <button key={k} type="button" className={`${TB} ${p[k] ? on : OFF}`} onClick={() => set[k]((v) => !v)} aria-pressed={p[k]}
                data-testid={`network-toggle-${{ links: "links", showOlt: "olt", showSplit: "splitter", showCables: "cables" }[k]}`}><Icon className="h-4 w-4" />{label}</button>
      ))}
    </div>
  );
}

const INFRA_COLS = [["kode", "Kode"], ["jenis", "Jenis"], ["nama", "Nama"], ["wilayah", "Wilayah"], ["kapasitas", "Kapasitas"], ["terpakai", "Terpakai"],
  ["tersedia", "Tersedia"], ["status", "Status"]];
const CABLE_COLS = [["kode", "Kode"], ["jenis", "Jenis"], ["dari", "Dari"], ["ke", "Ke"], ["core", "Core"], ["panjang", "Panjang"], ["status", "Status"]];
const KIND_BG = { server: "bg-[#1E293B]", fdt: "bg-[#0055FF]", otb: "bg-[#0E7490]", odc: "bg-[#16A34A]", olt: "bg-[#7C3AED]", odp: "bg-[#0055FF]", splitter: "bg-[#0D9488]" };

function MobileSort({ cols, st, testid }) {
  const dir = st.sort?.dir;
  return (
    <div className="flex items-center gap-2 border-b bg-[#F8F9FA] px-3 py-2 md:hidden">
      <label className="text-xs font-semibold text-[#6B7280]" htmlFor={`${testid}-mobile-sort`}>Urutkan</label>
      <select id={`${testid}-mobile-sort`} className="h-9 min-w-0 flex-1 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" value={st.sort?.col || ""}
              onChange={(e) => st.setSort(e.target.value ? { col: e.target.value, dir: dir || "asc" } : null)} data-testid={`${testid}-mobile-sort`}>
        <option value="">Bawaan</option>{cols.map(([c, l]) => <option key={c} value={c}>{l}</option>)}</select>
      <Button size="sm" variant="outline" className="h-9" disabled={!st.sort} onClick={() => st.setSort({ ...st.sort, dir: dir === "asc" ? "desc" : "asc" })}
              data-testid={`${testid}-mobile-sort-dir`}>{dir === "desc" ? <ArrowDown className="mr-1 h-4 w-4" /> : <ArrowUp className="mr-1 h-4 w-4" />}{dir === "desc" ? "Turun" : "Naik"}</Button>
    </div>
  );
}

const infraNums = (r) => (CT[r.kind] ? `${r.member_count} anggota` : r.kind === "otb" ? `${r.jumlah_core} core` : r.kind === "odp" ? `${r.port_terpakai}/${r.kapasitas_port} port terpakai · ${r.port_kosong} tersedia`
  : r.kind === "olt" ? `${r.port_terpakai}/${r.port_count} PON terpakai`
  : r.kind === "splitter" ? `${r.rasio} · ${r.output_terpakai}/${r.jumlah_output} output terpakai` : `${r.kapasitas_splitter ? `${r.kapasitas_splitter} splitter · ` : ""}${r.odp_count} ODP`);

function InfraCards({ rows, onView, wilayahName, me, onDeleted, q }) {
  return (
    <ul className="max-h-[28rem] divide-y overflow-y-auto md:hidden" data-testid="network-infra-cards">
      {rows.map((r) => (
        <li key={r.id} className="flex items-start gap-2 p-3" data-testid={`network-card-${r.kode}`}>
          <button type="button" className="min-w-0 flex-1 space-y-1 text-left" onClick={() => onView(r)} data-testid={`network-card-view-${r.kode}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="break-all font-mono-tab text-sm font-semibold">{r.kode}<OldKodeLabel row={r} q={q} testid={`network-card-oldkode-${r.kode}`} /></span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${KIND_BG[r.kind]}`}>{r.kind.toUpperCase()}</span>
              <StatusPill status={r.status} />
            </div>
            <div className="break-words text-sm text-[#111827]">{r.nama}</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#6B7280]">
              <span className={r.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(r.wilayah_id)}</span><span>·</span><span>{infraNums(r)}</span>
              {(r.in_server || !r.has_coords) && <LocCell row={r} testid={`network-card-nocoord-${r.kode}`} />}
            </div>
          </button>
          {!CT[r.kind] && <DeleteInfraButton iconOnly kind={r.kind} item={r} me={me} onDeleted={onDeleted} tid={`${r.kind}-card-delete-${r.kode}`} />}
        </li>
      ))}
      {!rows.length && <li className="p-4 text-center text-sm text-[#6B7280]">Tidak ada data untuk filter ini.</li>}
    </ul>
  );
}

function CableCards({ rows, onView, disabled, me, onDeleted, q }) {
  return (
    <ul className="max-h-[28rem] divide-y overflow-y-auto md:hidden" data-testid="network-cable-cards">
      {rows.map((c) => (
        <li key={c.id} className="flex items-start gap-2 p-3" data-testid={`cable-card-${c.kode}`}>
          <button type="button" className="min-w-0 flex-1 space-y-1 text-left disabled:opacity-60" onClick={() => onView(c)} disabled={disabled}
                  title={disabled ? "Selesaikan atau batalkan Gambar Jalur dulu" : "Lihat detail"} data-testid={`cable-card-view-${c.kode}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="break-all font-mono-tab text-sm font-semibold">{c.kode}<OldKodeLabel row={c} q={q} testid={`cable-card-oldkode-${c.kode}`} /></span>
              <span className="rounded bg-[#FFF7ED] px-1.5 py-0.5 text-[10px] font-bold text-[#C2410C]">{CABLE_JENIS_LABEL[c.jenis]}</span>
              <StatusPill status={c.status} />
            </div>
            <div className="break-words text-sm"><span className="text-xs text-[#6B7280]">Dari: </span>{c.from?.kode || "-"}{c.from?.nama ? ` — ${c.from.nama}` : ""}</div>
            <div className="break-words text-sm"><span className="text-xs text-[#6B7280]">Ke: </span>{c.to?.kode || "-"}{c.to?.nama ? ` — ${c.to.nama}` : ""}</div>
            <div className="text-xs text-[#6B7280]" data-testid={`cable-card-length-${c.kode}`}>{c.jumlah_core ? `${c.jumlah_core} core · ` : ""}{cableLen(c)}</div>
          </button>
          {!disabled && <DeleteInfraButton iconOnly kind="cable" item={c} me={me} onDeleted={onDeleted} tid={`cable-card-delete-${c.kode}`} />}
        </li>
      ))}
      {!rows.length && <li className="p-4 text-center text-sm text-[#6B7280]">Belum ada jalur kabel untuk filter ini.</li>}
    </ul>
  );
}

function InfraTable({ rows: raw, onView, wilayahName, me, onDeleted, q }) {
  const getters = useMemo(() => ({ kode: (r) => r.kode, jenis: (r) => r.kind, nama: (r) => r.nama, wilayah: (r) => wilayahName(r.wilayah_id),
    kapasitas: (r) => (CT[r.kind] ? null : r.kind === "otb" ? r.jumlah_core : r.kind === "olt" ? r.port_count : r.kind === "splitter" ? r.jumlah_output : r.kind === "odc" ? r.kapasitas_splitter : r.kapasitas_port),
    terpakai: (r) => (r.kind === "odp" && r.kapasitas_port ? r.port_terpakai / r.kapasitas_port : CT[r.kind] ? r.member_count : null),
    tersedia: (r) => (r.kind === "odp" ? r.port_kosong : null), status: (r) => r.status }), [wilayahName]);
  const st = useTableSort("map-infra", raw, getters);
  const rows = st.sorted;
  return (
    <>
    <MobileSort cols={INFRA_COLS} st={st} testid="network-table" />
    <InfraCards rows={rows} onView={onView} wilayahName={wilayahName} me={me} onDeleted={onDeleted} q={q} />
    <div className="hidden max-h-64 overflow-auto md:block" data-testid="network-infra-table">
      <table className="w-full min-w-[760px] text-xs">
        <thead className="sticky top-0 bg-[#F8F9FA] text-left uppercase text-[#6B7280]">
          <tr>{INFRA_COLS.map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="network-table" />)}
            <th>Peta</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`network-row-${r.kode}`}>
              <td className="whitespace-nowrap p-2 font-mono-tab font-semibold">{r.kode}<OldKodeLabel row={r} q={q} testid={`network-row-oldkode-${r.kode}`} /></td>
              <td><span className={`rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${KIND_BG[r.kind]}`}>{r.kind.toUpperCase()}</span></td>
              <td>{r.nama}</td><td className={r.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(r.wilayah_id)}</td>
              <td>{CT[r.kind] ? "-" : r.kind === "otb" ? `${r.jumlah_core} core` : r.kind === "olt" ? `${r.port_count} PON` : r.kind === "splitter" ? `${r.rasio} (${r.jumlah_output})` : r.kind === "odc" ? (r.kapasitas_splitter ? `${r.kapasitas_splitter} splitter` : "-") : `${r.kapasitas_port} port`}</td>
              <td data-testid={`network-row-used-${r.kode}`}>{CT[r.kind] ? `${r.member_count} anggota` : r.kind === "odp" ? `${r.port_terpakai} (${pct(r.port_terpakai, r.kapasitas_port)}%)` : r.kind === "olt" ? `${r.port_terpakai} PON`
                : r.kind === "splitter" ? `${r.output_terpakai} output` : r.kind === "otb" ? "-" : `${r.odp_count} ODP`}</td>
              <td>{r.kind === "odp" ? r.port_kosong : r.kind === "olt" ? r.port_count - r.port_terpakai : r.kind === "splitter" ? r.jumlah_output - r.output_terpakai : "-"}</td>
              <td><StatusPill status={r.status} /></td>
              <td><LocCell row={r} testid={`network-nocoord-${r.kode}`} /></td>
              <td className="whitespace-nowrap"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onView(r)} data-testid={`network-view-${r.kode}`}><Eye className="h-3.5 w-3.5" /></Button>
                {!CT[r.kind] && <DeleteInfraButton iconOnly kind={r.kind} item={r} me={me} onDeleted={onDeleted} />}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={10} className="p-4 text-center text-[#6B7280]">Tidak ada data untuk filter ini.</td></tr>}
        </tbody>
      </table>
    </div>
    </>
  );
}

function CableTable({ rows: raw, onView, disabled, me, onDeleted, q }) {
  const st = useTableSort("map-cable", raw, CABLE_GETTERS);
  const rows = st.sorted;
  return (
    <>
    <MobileSort cols={CABLE_COLS} st={st} testid="cable-table" />
    <CableCards rows={rows} onView={onView} disabled={disabled} me={me} onDeleted={onDeleted} q={q} />
    <div className="hidden max-h-64 overflow-auto md:block" data-testid="network-cable-table">
      <table className="w-full min-w-[760px] text-xs">
        <thead className="sticky top-0 bg-[#F8F9FA] text-left uppercase text-[#6B7280]">
          <tr>{CABLE_COLS
            .map(([c, l], i) => <SortTh key={c} className={i ? "" : "p-2"} col={c} label={l} s={st} testid="cable-table" />)}<th /></tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t" data-testid={`cable-row-${c.kode}`}>
              <td className="whitespace-nowrap p-2 font-mono-tab font-semibold">{c.kode}<OldKodeLabel row={c} q={q} testid={`cable-row-oldkode-${c.kode}`} /></td>
              <td>{CABLE_JENIS_LABEL[c.jenis]}</td><td>{c.from?.kode || "-"}</td><td>{c.to?.kode || "-"}</td>
              <td>{c.jumlah_core || "-"}</td><td data-testid={`cable-row-length-${c.kode}`}>{cableLen(c)}</td><td><StatusPill status={c.status} /></td>
              <td><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onView(c)} disabled={disabled} title={disabled ? "Selesaikan atau batalkan Gambar Jalur dulu" : "Lihat detail"} data-testid={`cable-view-${c.kode}`}><Eye className="h-3.5 w-3.5" /></Button>
                {!disabled && <DeleteInfraButton iconOnly kind="cable" item={c} me={me} onDeleted={onDeleted} />}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={8} className="p-4 text-center text-[#6B7280]">Belum ada jalur kabel untuk filter ini.</td></tr>}
        </tbody>
      </table>
    </div>
    </>
  );
}

const pt = (kind, o) => ({ kind, id: o.id, kode: o.kode, wilayah_id: o.wilayah_id, latitude: o.latitude, longitude: o.longitude, in_server: o.in_server || null });

export default function NetworkMap() {
  const { refs, error, wilayahName, lokasiName } = useNetworkRefs();
  const { user } = useAuth();
  const storeKey = `network_map_lokasi:${user?.id || ""}`;
  const [lokasi, setLokasiRaw] = useState(() => sessionStorage.getItem(storeKey));
  const setLokasi = (v) => { setLokasiRaw(v); if (v) sessionStorage.setItem(storeKey, v); else sessionStorage.removeItem(storeKey); };
  const [f, setF] = useState({ jenis: "", wilayah_id: "", status: "", odc_id: "", q: "", teknologi: "", server_id: "", fdt_id: "" });
  const [srvSet, setSrvSet] = useState(null);
  const [oltData, setOltData] = useState({ olt: [], links: [], splitters: [], split_in: [], split_out: [], split_src: [] });
  const [showOlt, setShowOlt] = useState(true);
  const [showSplit, setShowSplit] = useState(true);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sel, setSel] = useState(null);
  const [links, setLinks] = useState(true);
  const [sat, setSat] = useState(false);
  const [cables, setCables] = useState([]);
  const [showCables, setShowCables] = useState(true);
  const [draft, setDraft] = useState(null);
  const [ctPick, setCtPick] = useState(null);
  const [openInfra, setOpenInfra] = useState(true);
  const [openCable, setOpenCable] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [cableKey, setCableKey] = useState(0);
  const [focus, setFocus] = useState(null);
  const [panel, setPanel] = useState(null);
  const [move, setMove] = useState(false);
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", esc);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", esc); };
  }, [full]);
  const [saving, setSaving] = useState(false);
  const [locKey, setLocKey] = useState(0);
  const [opticalPath, setOpticalPath] = useState(null);
  useEffect(() => {
    setOpticalPath(null);
    if (sel?.kind !== "odp") return;
    api.get(`/network/odp/${sel.id}/optical-path`).then((r) => setOpticalPath(r.data)).catch(() => setOpticalPath({ error: true, nodes: [] }));
  }, [sel?.kind, sel?.id, locKey, data]);  // data: muat ulang setelah relasi/keanggotaan berubah
  const mapBox = useRef(null);
  const panelBox = useRef(null);
  const frameBox = useRef(null);
  const [panelTop, setPanelTop] = useState(null);  // desktop: panel melayang mulai dari tepi atas bingkai peta
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const upd = () => setPanelTop(mq.matches && frameBox.current ? frameBox.current.offsetTop : null);
    const ro = new ResizeObserver(upd);
    if (mapBox.current) ro.observe(mapBox.current);
    mq.addEventListener("change", upd);
    upd();
    return () => { ro.disconnect(); mq.removeEventListener("change", upd); };
  });
  useEffect(() => {
    if (!refs) return;
    if (lokasi && lokasi !== ALL && !refs.locations.some((l) => l.id === lokasi && l.active)) { setLokasi(null); return; }
    if (lokasi === null && refs.default_lokasi_id) setLokasiRaw(refs.default_lokasi_id);
  }, [refs, lokasi]); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    if (!lokasi) return;
    api.get("/network/map", { params: { lokasi_id: lokasi === ALL ? undefined : lokasi } }).then((r) => setData(r.data));
    api.get("/network/summary").then((r) => setSummary(r.data));
    api.get("/network/cables", { params: { lokasi_id: lokasi === ALL ? undefined : lokasi } }).then((r) => setCables(r.data));
    api.get("/network/olt-map", { params: { lokasi_id: lokasi === ALL ? undefined : lokasi } }).then((r) => setOltData(r.data));
  }, [lokasi]);
  useEffect(() => { load(); }, [load]);

  const cid = f.server_id || f.fdt_id;
  useEffect(() => {  // filter SERVER/FDT: set "kind:id" anggota dari detail kontainer (baca saja)
    if (!cid) return setSrvSet(null);
    setSrvSet(new Set());
    api.get(`/network/${f.fdt_id ? "fdt" : "servers"}/${cid}`).then((r) => setSrvSet(new Set(r.data.members.map((m) => `${m.kind}:${m.id}`)))).catch(() => setSrvSet(new Set()));
  }, [cid, f.fdt_id, data]);
  const mem = useCallback((kind, id) => !srvSet || srvSet.has(`${kind}:${id}`), [srvSet]);
  const filtered = useMemo(() => {
    if (!data) return { odc: [], odp: [], otb: [], server: [], fdt: [] };
    if (cid) {
      const q = f.q.trim().toLowerCase();
      const ok = (o) => (!f.wilayah_id || o.wilayah_id === f.wilayah_id) && (!f.status || o.status === f.status)
        && (!q || [o.kode, o.nama, o.alamat, ...(o.kode_lama || [])].some((v) => (v || "").toLowerCase().includes(q)));
      const only = (k) => f.jenis && f.jenis !== k;
      return { odc: only("odc") || f.teknologi ? [] : data.odc.filter((o) => ok(o) && mem("odc", o.id) && (!f.odc_id || o.id === f.odc_id)),
        odp: only("odp") || f.teknologi ? [] : data.odp.filter((o) => ok(o) && mem("odp", o.id) && (!f.odc_id || o.odc_id === f.odc_id)),
        otb: only("otb") || f.odc_id ? [] : (data.otb || []).filter((o) => ok(o) && mem("otb", o.id)),
        server: only("server") || f.odc_id || f.teknologi ? [] : (data.server || []).filter((o) => ok(o) && o.id === f.server_id),
        fdt: only("fdt") || f.odc_id || f.teknologi ? [] : (data.fdt || []).filter((o) => ok(o) && o.id === f.fdt_id) };
    }
    const q = f.q.trim().toLowerCase();
    const ok = (o) => (!f.wilayah_id || o.wilayah_id === f.wilayah_id) && (!f.status || o.status === f.status)
      && (!q || [o.kode, o.nama, o.alamat, ...(o.kode_lama || [])].some((v) => (v || "").toLowerCase().includes(q)));
    return {
      odc: ["odp", "olt", "splitter", "otb", "server", "fdt"].includes(f.jenis) ? [] : data.odc.filter((o) => ok(o) && (!f.odc_id || o.id === f.odc_id)),
      odp: ["odc", "olt", "splitter", "otb", "server", "fdt"].includes(f.jenis) ? [] : data.odp.filter((o) => ok(o) && (!f.odc_id || o.odc_id === f.odc_id)),
      otb: (f.jenis && f.jenis !== "otb") || f.odc_id ? [] : (data.otb || []).filter(ok),
      server: (f.jenis && f.jenis !== "server") || f.odc_id || f.teknologi ? [] : (data.server || []).filter(ok),
      fdt: (f.jenis && f.jenis !== "fdt") || f.odc_id || f.teknologi ? [] : (data.fdt || []).filter(ok),
    };
  }, [data, f, mem, cid]);
  const oltRows = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    if (f.jenis && f.jenis !== "olt") return [];
    return oltData.olt.filter((o) => (!f.wilayah_id || o.wilayah_id === f.wilayah_id) && (!f.status || o.status === f.status)
      && (!f.teknologi || o.teknologi === f.teknologi) && (!f.odc_id || oltData.links.some((l) => l.olt_id === o.id && l.odc_id === f.odc_id))
      && (!q || [o.kode, o.nama, o.model, o.serial_number, ...(o.kode_lama || [])].some((v) => (v || "").toLowerCase().includes(q))) && mem("olt", o.id));
  }, [oltData, f, mem]);
  const splitRows = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    if ((f.jenis && f.jenis !== "splitter") || f.teknologi) return [];
    return oltData.splitters.filter((x) => (!f.wilayah_id || x.wilayah_id === f.wilayah_id) && (!f.status || x.status === f.status)
      && (!f.odc_id || oltData.split_out.some((o) => o.splitter_id === x.id && o.odc_id === f.odc_id))
      && (!q || [x.kode, x.nama, x.lokasi_fisik, ...(x.kode_lama || [])].some((v) => (v || "").toLowerCase().includes(q))) && mem("splitter", x.id));
  }, [oltData, f, mem]);
  const srvLinks = useMemo(() => (!cid ? oltData : {  // filter SERVER/FDT: hanya relasi yang kedua ujungnya anggota
    links: oltData.links.filter((l) => mem("olt", l.olt_id) && mem("odc", l.odc_id)),
    split_src: (oltData.split_src || []).filter((i) => mem(i.source_kind, i.source_id) && mem("splitter", i.splitter_id)),
    split_in: oltData.split_in.filter((i) => mem("splitter", i.splitter_id) && mem("olt", i.olt_id)),
    split_out: oltData.split_out.filter((o) => mem("splitter", o.splitter_id) && mem(o.target_kind, o.target_kind === "odc" ? o.odc_id : o.target_id)),
  }), [oltData, cid, mem]);
  const cableRows = useMemo(() => {
    const q = f.q.trim().toLowerCase();
    return cables.filter((c) => (!f.status || c.status === f.status)
      && (!f.wilayah_id || [c.from?.wilayah_id, c.to?.wilayah_id].includes(f.wilayah_id))
      && (!q || [c.kode, c.nama, ...(c.kode_lama || [])].some((v) => (v || "").toLowerCase().includes(q)))
      && (!cid || (mem(c.from_kind, c.from_id) && mem(c.to_kind || "odp", c.to_id))));
  }, [cables, f, mem, cid]);
  const findInfra = (s) => (s.kind === "odc" ? [...data.odc, ...data.odc_outside_filter] : s.kind === "otb" ? data.otb || [] : s.kind === "server" ? data.server || [] : s.kind === "fdt" ? data.fdt || [] : data.odp).find((o) => o.id === s.id);
  const endpoints = useMemo(() => (!data ? [] : [...data.odc.map((o) => ({ ...o, kind: "odc" })), ...data.odp.map((o) => ({ ...o, kind: "odp" })),
    ...(data.otb || []).map((o) => ({ ...o, kind: "otb" }))]), [data]);
  const pick = (s) => {
    if (full) {  // detail perangkat tampil di luar area peta: keluar layar penuh & arahkan ke panel di HP
      setFull(false);
      if (window.innerWidth < 1024) setTimeout(() => panelBox.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
    if (!draft) return setSel(s);
    if (CT[s.kind]) { const c = findInfra(s); return c && setCtPick({ ...c, label: s.kind === "fdt" ? "FDT" : "SERVER" }); }
    if (["cable", "olt", "splitter"].includes(s.kind)) return;
    const o = findInfra(s);
    if (o) choose(s, o);
  };
  const choose = (s, o) => {  // endpoint = perangkat asli (kind+id); koordinat efektif hanya untuk garis
    const block = endpointBlock(o);
    if (block) return toast.error(`${o.kode}: ${block}. Tidak dapat dijadikan ujung kabel.`);
    setCtPick(null);
    if (!draft.from) return step((d) => ({ ...d, from: pt(s.kind, o) }));
    if (draft.to) return;
    if (s.kind === "odc" && draft.from.kind !== "otb") return toast.error("Tujuan ODC hanya boleh dari OTB; tujuan lain: ODP atau OTB");
    if (draft.from.kind === s.kind && draft.from.id === o.id) return toast.error("Titik tujuan tidak boleh sama dengan titik awal");
    step((d) => ({ ...d, to: pt(s.kind, o) }));
    if (!draft.editing) setFormOpen(true);
  };
  const scrollToMap = () => {
    const r = mapBox.current?.getBoundingClientRect();
    if (r && (window.innerWidth < 1024 || r.top < 0 || r.bottom > window.innerHeight)) mapBox.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const watch = useRef(null);
  const stopWatch = () => { watch.current?.(); watch.current = null; };
  useEffect(() => stopWatch, []);
  const noFocus = (msg) => {
    toast.info(msg);
    if (window.innerWidth >= 1024) return scrollToMap();
    // Panel detail di bawah peta: gulir ke panel hanya bila di luar layar; cek ulang saat tinggi panel berubah (data detail dimuat).
    const el = panelBox.current;
    if (!el) return;
    const check = () => {
      const r = el.getBoundingClientRect();
      if (r.top < 64 || r.top > window.innerHeight - 120) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const ro = new ResizeObserver(check);
    const t1 = setTimeout(() => ro.observe(el), 80);
    const t2 = setTimeout(stopWatch, 1500);
    watch.current = () => { clearTimeout(t1); clearTimeout(t2); ro.disconnect(); };
    ["wheel", "touchmove"].forEach((ev) => window.addEventListener(ev, stopWatch, { once: true, passive: true }));
  };
  const focusSel = (s) => {
    stopWatch();
    setSel(s);
    let o = null;
    if (s.kind === "cable") {
      const c = cables.find((x) => x.id === s.id);
      if (c?.same_container) return noFocus(`Kedua ujung berada di ${c.same_container.label} ${c.same_container.kode} yang sama — jalur tidak digambar`);
      if (!c?.path?.length) return noFocus("Jalur kabel belum memiliki rute lengkap");
      setShowCables(true);
      const lats = c.path.map((p) => p[0]); const lngs = c.path.map((p) => p[1]);
      o = { latlng: c.path[Math.floor(c.path.length / 2)], bounds: [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]] };
    } else {
      const node = s.kind === "olt" ? oltData.olt.find((x) => x.id === s.id) : s.kind === "splitter" ? oltData.splitters.find((x) => x.id === s.id) : data && findInfra(s);
      if (node?.has_coords) {
        if (s.kind === "olt") setShowOlt(true);
        if (s.kind === "splitter") setShowSplit(true);
        o = { latlng: [node.latitude, node.longitude] };
      }
    }
    if (!o && move && s.kind !== "cable") { toast.info("Klik peta untuk menempatkan titik ini"); return scrollToMap(); }
    if (!o) return noFocus("Titik belum memiliki koordinat");
    scrollToMap();
    if (!centers.length) return;
    setFocus({ ...o, n: Date.now() });
  };
  const step = (fn) => setDraft((d) => ({ ...fn(d), hist: [...d.hist, { from: d.from, to: d.to, points: d.points }] }));
  const addBend = (p) => { if (draft?.from && !draft.to) step((d) => ({ ...d, points: [...d.points, p] })); };
  const moveBend = (i, p) => step((d) => ({ ...d, points: d.points.map((q, j) => (j === i ? p : q)) }));
  const undo = () => setDraft((d) => (d.hist.length ? { ...d, ...d.hist[d.hist.length - 1], hist: d.hist.slice(0, -1) }
    : d.to ? { ...d, to: null } : d.points.length ? { ...d, points: d.points.slice(0, -1) } : { ...d, from: null }));
  const showDrawArea = () => {
    setPanel(null);
    if (window.innerWidth < 1024) setTimeout(() => mapBox.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const nodeOf = (s) => (s.kind === "olt" ? oltData.olt.find((x) => x.id === s.id) : s.kind === "splitter" ? oltData.splitters.find((x) => x.id === s.id) : data && findInfra(s));
  const refreshLoc = () => { load(); setCableKey((k) => k + 1); setLocKey((k) => k + 1); };
  const undoLoc = async (kind, o, prev) => {
    try {
      await api.patch(`/network/${kind}/${o.id}/location`, { latitude: prev[0], longitude: prev[1] });
      toast.success(prev[0] == null ? `${o.kode} dikembalikan ke belum berkoordinat` : `Lokasi ${o.kode} dikembalikan ke posisi sebelumnya`);
      refreshLoc();
    } catch (err) { toast.error(`Gagal mengurungkan lokasi ${o.kode}: ${apiError(err)}`); }
  };
  const saveLoc = async (kind, o, latlng, revert) => {
    if (saving) { revert?.(); return toast.error("Tunggu penyimpanan sebelumnya selesai"); }
    setSaving(true);
    const prev = [o.latitude ?? null, o.longitude ?? null];
    try {
      await api.patch(`/network/${kind}/${o.id}/location`, { latitude: latlng[0], longitude: latlng[1] });
      toast.success(`Lokasi ${o.kode} tersimpan (${latlng[0]}, ${latlng[1]})`, {
        duration: 10000, action: { label: "Urungkan", onClick: () => undoLoc(kind, o, prev) } });
      refreshLoc();
    } catch (err) {
      revert?.();
      toast.error(`Gagal menyimpan lokasi ${o.kode}: ${apiError(err)}. Marker dikembalikan.`);
    } finally { setSaving(false); }
  };
  const selNode = move && sel && sel.kind !== "cable" ? nodeOf(sel) : null;
  const placeTarget = selNode && !selNode.has_coords && !selNode.in_server ? { ...selNode, kind: sel.kind } : null;
  const startDraw = () => { setCtPick(null); setMove(false); setSel(null); setShowCables(true); setDraft({ editing: null, from: null, points: [], to: null, hist: [] }); showDrawArea(); };
  const editCable = (c) => {
    setDraft({ editing: c, points: c.route || [], from: c.from && pt(c.from_kind, c.from), to: c.to && pt(c.to_kind || "odp", c.to), hist: [] });
    setSel(null);
    showDrawArea();
  };
  const onInfraDeleted = (item) => {
    if (sel?.id === item.id) { setSel(null); setFocus(null); }
    load(); setCableKey((k) => k + 1);
  };
  const onCableSaved = (c) => { setFormOpen(false); setDraft(null); load(); setCableKey((k) => k + 1); setSel({ kind: "cable", id: c.id }); };
  const rows = [...oltRows.map((o) => ({ ...o, kind: "olt" })), ...splitRows.map((x) => ({ ...x, kind: "splitter" })), ...filtered.odc.map((o) => ({ ...o, kind: "odc", odp_count: data.odp.filter((p) => p.odc_id === o.id).length })),
    ...filtered.server.map((o) => ({ ...o, kind: "server" })), ...filtered.fdt.map((o) => ({ ...o, kind: "fdt" })), ...filtered.otb.map((o) => ({ ...o, kind: "otb" })), ...filtered.odp.map((o) => ({ ...o, kind: "odp" }))];
  const lok = refs?.locations.find((l) => l.id === lokasi);
  const hasXY = (l) => l && l.latitude != null && l.longitude != null;
  const centers = !refs ? [] : lokasi === ALL
    ? refs.locations.filter((l) => l.active && hasXY(l)).map((l) => [l.latitude, l.longitude])
    : hasXY(lok) ? [[lok.latitude, lok.longitude]] : [];
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  if (error) return <div className="p-6 text-sm text-red-700" data-testid="network-error">{error}</div>;
  if (!refs) return <div className="p-6 text-sm">Memuat…</div>;
  return (
    <div className="flex h-full flex-col gap-3 p-4 sm:p-6 lg:p-2" data-testid="network-map-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Peta Jaringan</h1>
          <p className="text-sm text-[#6B7280]">Peta ODC, ODP dan infrastruktur jaringan AROMA NET</p></div>
        <div className="flex flex-wrap items-center gap-2">
          <select className={SELECT_CLS} value={lokasi || ""} onChange={(e) => { setLokasi(e.target.value || null); setSel(null); }} data-testid="network-lokasi-select">
            <option value="">— Pilih Lokasi —</option>
            {refs.locations.filter((l) => l.active).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            <option value={ALL}>Semua Lokasi</option>
          </select>
          <Input value={f.q} onChange={set("q")} placeholder="Cari ODC, ODP, alamat…" className="h-9 w-full sm:w-56" data-testid="network-search" />
        </div>
      </div>
      {!lokasi ? <LocationGate refs={refs} onPick={setLokasi} /> : (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="network-filters">
            <select className={SELECT_CLS} value={f.jenis} onChange={set("jenis")} data-testid="network-filter-jenis">
              <option value="">Semua Jenis</option><option value="olt">OLT</option><option value="otb">OTB</option><option value="server">SERVER</option><option value="fdt">FDT</option><option value="splitter">Splitter</option><option value="odc">ODC</option><option value="odp">ODP</option></select>
            <select className={SELECT_CLS} value={f.teknologi} onChange={set("teknologi")} data-testid="network-filter-teknologi">
              <option value="">Semua Teknologi OLT</option>{OLT_TECH.map((t) => <option key={t}>{t}</option>)}</select>
            <WilayahCombobox className="w-44" options={refs.wilayah} value={f.wilayah_id} allLabel="Semua Wilayah"
                             onChange={(v) => set("wilayah_id")({ target: { value: v } })} testid="network-filter-wilayah" />
            <select className={SELECT_CLS} value={f.status} onChange={set("status")} data-testid="network-filter-status">
              <option value="">Semua Status</option>{INFRA_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select>
            <select className={SELECT_CLS} value={f.odc_id} onChange={set("odc_id")} data-testid="network-filter-odc">
              <option value="">Semua ODC Induk</option>{(data?.odc || []).map((o) => <option key={o.id} value={o.id}>{o.kode}</option>)}</select>
            <select className={SELECT_CLS} value={f.server_id} onChange={(e) => setF((x) => ({ ...x, server_id: e.target.value, fdt_id: "" }))} data-testid="network-filter-server">
              <option value="">Semua SERVER</option>{(data?.server || []).map((o) => <option key={o.id} value={o.id}>{o.kode} · {o.nama}</option>)}</select>
            <select className={SELECT_CLS} value={f.fdt_id} onChange={(e) => setF((x) => ({ ...x, fdt_id: e.target.value, server_id: "" }))} data-testid="network-filter-fdt">
              <option value="">Semua FDT</option>{(data?.fdt || []).map((o) => <option key={o.id} value={o.id}>{o.kode} · {o.nama}</option>)}</select>
            <Button size="sm" variant="outline" onClick={() => setF({ jenis: "", wilayah_id: "", status: "", odc_id: "", q: "", teknologi: "", server_id: "", fdt_id: "" })} data-testid="network-filter-reset">
              <RotateCcw className="mr-1 h-3.5 w-3.5" />Reset Filter</Button>
            {summary && <span className="ml-auto text-xs text-[#6B7280]" data-testid="network-summary">
              {summary.odc_total} ODC · {summary.odp_total} ODP · <b className="text-[#B45309]">{summary.customers_unplaced}</b> pelanggan belum ditempatkan ke ODP
              ({summary.customers_unplaced_not_stopped} di luar status BERHENTI)</span>}
          </div>
          <div className="relative grid gap-3 lg:h-[max(560px,75vh)]">
            <div ref={mapBox} className="flex scroll-mt-16 flex-col gap-2 lg:h-full lg:min-h-0" data-testid="network-map-wrap">
              {centers.length > 0 && <MapToolbar {...{ panel, setPanel, sat, links, showOlt, showSplit, showCables }}
                                                 canDraw={!!refs.me?.can_manage && !draft && !move} onDraw={startDraw}
                                                 canMove={!!refs.me?.can_manage && !draft} move={move} onMove={() => setMove((v) => !v)} />}
              {move && centers.length > 0 && <MoveBar target={placeTarget} saving={saving} onDone={() => setMove(false)} />}
              {panel === "layer" && centers.length > 0 && <LayerPanel {...{ sat, setSat, links, setLinks, showOlt, setShowOlt, showSplit, setShowSplit, showCables, setShowCables }} />}
              {panel === "legend" && centers.length > 0 && <MapLegend />}
              {draft && <CableDrawBar draft={draft} onUndo={undo} onCancel={() => { setDraft(null); setFormOpen(false); setCtPick(null); }} onDone={() => setFormOpen(true)}
                                     endpoints={endpoints} onPickEndpoint={(o) => choose({ kind: o.kind, id: o.id }, o)} />}
              <div ref={frameBox} className={full ? "fixed inset-0 isolate z-[10000] h-[100dvh] w-screen overflow-hidden bg-white" : "relative isolate z-0 h-[460px] min-h-[300px] overflow-hidden rounded-lg border border-[#E5E7EB] lg:h-auto lg:flex-1"} data-testid="network-map-frame">
              {data && centers.length > 0 && <NetworkMapView odcs={filtered.odc} odps={filtered.odp} otbs={filtered.otb} servers={filtered.server} fdts={filtered.fdt} allOdc={[...data.odc, ...data.odc_outside_filter]}
                                       centers={centers} viewKey={lokasi} selected={sel} onSelect={pick} showLinks={links} satellite={sat}
                                       cables={cableRows} showCables={showCables} draft={draft} onMapClick={addBend} onMoveBend={moveBend}
                                       olts={oltRows} oltLinks={srvLinks.links} showOlt={showOlt} focus={focus}
                                       splitters={splitRows} splitIn={srvLinks.split_in} splitOut={srvLinks.split_out} splitSrc={srvLinks.split_src || []} showSplit={showSplit} opticalPath={sel?.kind === "odp" ? opticalPath : null}
                                       fullscreen={full} onFullscreen={setFull} onMoveNode={move ? saveLoc : null} onPlace={placeTarget ? (p) => saveLoc(placeTarget.kind, placeTarget, p) : null} />}
              {draft && ctPick && <ContainerPickPanel container={ctPick} items={endpoints} onPick={(o) => choose({ kind: o.kind, id: o.id }, o)} onClose={() => setCtPick(null)} />}
              {!centers.length && (
                <div className="flex h-full items-center justify-center bg-[#F8F9FA] p-6 text-center text-sm text-[#92400E]" data-testid="network-office-nocoord">
                  Koordinat kantor {lokasi === ALL ? "" : `"${lok?.name || "-"}" `}belum dikonfigurasi, sehingga peta tidak dapat dipusatkan.
                  Admin perlu melengkapi latitude/longitude di Pengaturan Aplikasi → Lokasi Absensi. Data tetap tersedia di daftar di bawah.
                </div>
              )}
              </div>
            </div>
            {/* Desktop: panel melayang di kanan atas peta, hanya saat ada perangkat dipilih. HP: tetap di bawah peta. */}
            <div ref={panelBox} data-testid="network-detail-panel"
                 style={panelTop != null ? { top: panelTop + 4, height: `calc(100% - ${panelTop + 8}px)` } : undefined}
                 className={`max-h-[560px] scroll-mt-16 overflow-hidden rounded-lg border border-[#E5E7EB] lg:absolute lg:right-3 lg:z-[600] lg:max-h-none lg:w-[400px] lg:bg-white lg:shadow-2xl ${sel ? "" : "lg:hidden"}`}>
              {sel?.kind === "olt" ? (
                <aside className="flex h-full flex-col bg-white" data-testid="olt-map-panel">
                  <div className="flex items-center justify-between border-b p-3"><b>Detail OLT</b>
                    <Button size="icon" variant="ghost" onClick={() => setSel(null)} data-testid="olt-panel-close"><X className="h-4 w-4" /></Button></div>
                  <div className="flex-1 overflow-y-auto p-3"><OltDetail key={`${sel.id}-${locKey}`} oltId={sel.id} canManage={!!refs.me?.can_manage} wilayahName={wilayahName}
                                                                       lokasiName={lokasiName} onChanged={load} me={refs.me} onDeleted={onInfraDeleted} onSelect={focusSel} /></div>
                </aside>)
                : sel?.kind === "splitter" ? (
                <aside className="flex h-full flex-col bg-white" data-testid="splitter-map-panel">
                  <div className="flex items-center justify-between border-b p-3"><b>Detail Splitter</b>
                    <Button size="icon" variant="ghost" onClick={() => setSel(null)} data-testid="splitter-panel-close"><X className="h-4 w-4" /></Button></div>
                  <div className="flex-1 overflow-y-auto p-3"><SplitterDetail key={`${sel.id}-${locKey}`} splitterId={sel.id} canManage={!!refs.me?.can_manage} wilayahName={wilayahName}
                                                                            me={refs.me} onChanged={load} onDeleted={onInfraDeleted} onSelect={focusSel} /></div>
                </aside>)
                : sel?.kind === "otb" ? (
                <aside className="flex h-full flex-col bg-white" data-testid="otb-map-panel">
                  <div className="flex items-center justify-between border-b p-3"><b>Detail OTB</b>
                    <Button size="icon" variant="ghost" onClick={() => setSel(null)} data-testid="otb-panel-close"><X className="h-4 w-4" /></Button></div>
                  <div className="flex-1 overflow-y-auto p-3"><OtbDetail key={`${sel.id}-${locKey}`} otbId={sel.id} me={refs.me} wilayahName={wilayahName}
                                                                      reloadKey={cableKey} onSelect={focusSel} onDeleted={onInfraDeleted} /></div>
                </aside>)
                : sel?.kind === "server" ? (
                <aside className="flex h-full flex-col bg-white" data-testid="server-map-panel">
                  <div className="flex items-center justify-between border-b p-3"><b>Detail SERVER</b>
                    <Button size="icon" variant="ghost" onClick={() => setSel(null)} data-testid="server-panel-close"><X className="h-4 w-4" /></Button></div>
                  <div className="flex-1 overflow-y-auto p-3"><ServerDetail key={`${sel.id}-${locKey}`} serverId={sel.id} me={refs.me} wilayahName={wilayahName} lokasiName={lokasiName}
                    onSelect={focusSel} onChanged={load} onArchived={onInfraDeleted}
                    extra={<Button size="sm" variant="outline" asChild data-testid="server-open-master"><Link to={`/network/server?server_id=${sel.id}`}>Buka di Master SERVER</Link></Button>} /></div>
                </aside>)
                : sel?.kind === "fdt" ? (
                <aside className="flex h-full flex-col bg-white" data-testid="fdt-map-panel">
                  <div className="flex items-center justify-between border-b p-3"><b>Detail FDT</b>
                    <Button size="icon" variant="ghost" onClick={() => setSel(null)} data-testid="fdt-panel-close"><X className="h-4 w-4" /></Button></div>
                  <div className="flex-1 overflow-y-auto p-3"><FdtDetail key={`${sel.id}-${locKey}`} serverId={sel.id} me={refs.me} wilayahName={wilayahName} lokasiName={lokasiName}
                    onSelect={focusSel} onChanged={load} onArchived={onInfraDeleted}
                    extra={<Button size="sm" variant="outline" asChild data-testid="fdt-open-master"><Link to={`/network/fdt?fdt_id=${sel.id}`}>Buka di Master FDT</Link></Button>} /></div>
                </aside>)
                : sel?.kind === "cable" ? <CableDetailPanel sel={sel} me={refs.me} wilayahName={wilayahName} reloadKey={cableKey}
                                                         onClose={() => setSel(null)} onSelect={focusSel} onEdit={editCable} onDeleted={onInfraDeleted} />
                : sel ? <InfraDetailPanel key={`${sel.kind}-${sel.id}-${locKey}`} sel={sel} me={refs.me} wilayahName={wilayahName} lokasiName={lokasiName}
                                       onClose={() => setSel(null)} onSelect={focusSel} onChanged={load} onDeleted={onInfraDeleted}
                                       opticalPath={sel.kind === "odp" ? opticalPath : undefined} />
                : <div className="flex h-full items-center justify-center p-6 text-center text-sm text-[#6B7280]" data-testid="network-detail-empty">
                    Pilih titik SERVER/FDT/OLT/OTB/Splitter/ODC/ODP di peta atau dari daftar untuk melihat detail.</div>}
            </div>
          </div>
          <div className="rounded-lg border border-[#E5E7EB] bg-white">
            <div className="flex items-center justify-between gap-2 border-b p-3"><span className="font-bold">Daftar Infrastruktur Jaringan <span className="text-xs font-normal text-[#6B7280]">
              ({rows.length}) — titik tanpa koordinat tidak tampil di peta</span></span>
              <CollapseBtn open={openInfra} onClick={() => setOpenInfra((v) => !v)} tid="network-infra-toggle" /></div>
            <div className={openInfra ? "" : "lg:hidden"}>
              <InfraTable rows={rows} onView={(r) => focusSel({ kind: r.kind, id: r.id })} wilayahName={wilayahName} me={refs.me} onDeleted={onInfraDeleted} q={f.q} /></div>
          </div>
          <div className="rounded-lg border border-[#E5E7EB] bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
              <span className="font-bold">Daftar Jalur Kabel <span className="text-xs font-normal text-[#6B7280]">({cableRows.length})</span></span>
              <Button size="sm" variant="outline" disabled={!cableRows.some((c) => c.path?.length)} data-testid="cable-export-kml"
                      title="Ekspor kabel sesuai filter aktif (yang koordinatnya lengkap)"
                      onClick={() => downloadKml(cableRows.filter((c) => c.path?.length).map((c) => c.id), "jalur-kabel-aromanet.kml")}>
                <Download className="mr-1 h-3.5 w-3.5" />Ekspor KML</Button>
              <CollapseBtn open={openCable} onClick={() => setOpenCable((v) => !v)} tid="network-cable-toggle" />
            </div>
            <div className={openCable ? "" : "lg:hidden"}>
              <CableTable rows={cableRows} disabled={!!draft} onView={(c) => focusSel({ kind: "cable", id: c.id })} me={refs.me} onDeleted={onInfraDeleted} q={f.q} /></div>
          </div>
          <CableFormDialog draft={draft?.from && draft?.to ? draft : null} open={formOpen && !!draft?.to} onClose={() => setFormOpen(false)} onSaved={onCableSaved} />
        </>
      )}
    </div>
  );
}
